
    import Vue from "vue";
    import {Component, Prop} from "vue-property-decorator";
    import {VueInject} from "@/VueIntegration/VueInjectable";
    import {
      ICompletedAttemptResult,
      TutorialAttemptApi
    } from "@/Api/TutorialAttemptApi";
    import VueButton from "../UI/VueButton.vue";
    import {Slideshow} from "@/slideshow";
    import Checkbox from "../Forms/Checkbox.vue";
    import Modal from "../UI/Modal.vue";
    import {Voiceover} from "@/voiceover";

    enum State {
      Initial = 0,
      SubmittingResults = 1,
      CollectingFeedback = 2,
      SubmittingFeedback = 3,
      Completed = 4,
      Passed = 5,
      Failed = 6,
      Error = -1,
    }

    let model = {
      state: <State> State.Initial,
      isCompleted: false,
      isPassed: <boolean|null> null,
      passPercent: 0,
      markPercent: 0,
      isApprovedByUser: false,
      feedback: ""
    };

    @Component({
        components: {Modal, Checkbox, VueButton}
    })
    export default class EndSlide extends Vue {
        @VueInject("TutorialAttemptApi") readonly api: TutorialAttemptApi;
        @VueInject("Slideshow") slideshow: Slideshow;
        @VueInject("Voiceover") voiceover: Voiceover;

        @Prop({default: false}) collectFeedback: boolean;
        @Prop({default: false}) feedbackDescription: string|boolean;

        promise: Promise<ICompletedAttemptResult>;

        // we want to keep the model in memory if the user navigates back from the end slide
        model = model;

        showErrorModal = false;

        get attempt() {
            return this.slideshow.attempt;
        }

        get appearance() {
            return this.slideshow.appearance;
        }

        get variables() {
            return this.slideshow.variables;
        }

        mounted() {
            if (model.state != State.Initial) {
                return;
            }

            this.voiceover.announceAfterCurrentSlideVoiceover("Submitting your results.");
            model.state = State.SubmittingResults;
            this.promise = this.api.endTutorialAttempt();
            this.promise.then((result) => {
                if (result.completed) {
                    if (result.passInformation != null) {
                        model.isPassed = result.passInformation.passed;
                        model.markPercent = result.passInformation.markPercent;
                        model.passPercent = result.passInformation.passPercent;
                    }

                    if (this.attempt.isApproval) {
                        model.state = State.CollectingFeedback;
                    }
                    else if (result.passInformation != null) {
                        model.state = model.isPassed ? State.Passed : State.Failed;
                        this.announceFinished();
                    }
                    else {
                        model.state = State.Completed;
                        this.announceFinished();
                    }
                }
                else {
                    model.state = State.Error;
                    this.announceError();
                }
            });

            this.promise.catch(() => {
                model.state = State.Error;
                this.announceError();
            });

            this.slideshow.slideEntered();
        }

        retry() {
            window.location.hash = "";
            window.location.reload();
        }

        exit() {
            this.slideshow.exit();
        }

        announceFinished() {
            if (model.state === State.Completed)
                this.announceCompleted();
            if (model.state === State.Passed)
                this.announcePassed();
            if (model.state === State.Failed)
                this.announceFailure();
        }

        announceCompleted() {
            this.voiceover.announceAfterCurrentSlideVoiceover("Tutorial completed. Please use the exit button in the main region of the page to return to the dashboard.");
        }

        announcePassed() {
            this.voiceover.announceAfterCurrentSlideVoiceover("Tutorial passed. Please use the exit button in the main region of the page to return to the dashboard.");
        }

        announceFailure() {
            this.voiceover.announceAfterCurrentSlideVoiceover("Tutorial failed. Please use the Try Again button to restart the tutorial or the exit button in the main region of the page to return to the dashboard.");
        }

        announceFeedbackForm() {
            this.voiceover.announceAfterCurrentSlideVoiceover("Please fill out the feedback form in the main region of the page before exiting.");
        }

        announceError() {
            this.voiceover.announceAfterCurrentSlideVoiceover("Sorry, something went wrong. Please try refreshing the page. If the error persists, please contact " + this.slideshow.variables.customer.supportEmail);
        }

        submitFeedback() {
            model.state = State.SubmittingFeedback;
            this.api.submitTutorialApproval(model.isApprovedByUser, model.feedback)
                .then(() => {
                    if (model.isPassed != null) {
                        model.state = model.isPassed ? State.Passed : State.Failed;
                        this.announceFinished();
                    }
                    else {
                        model.state = State.Completed;
                        this.announceFinished();
                    }
                })
                .catch(() => {
                    model.state = State.Error;
                    this.announceError();
                });
        }
    }
